import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { CheckCircle, Loader, XCircle } from 'lucide-react';

export default function VerifySalepage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('กำลังยืนยันอีเมล...');

  useEffect(() => {
    const verifyPayment = async () => {
      const userId = searchParams.get('userId');
      const paymentId = searchParams.get('paymentId');

      if (!userId || !paymentId) {
        setStatus('error');
        setMessage('ลิงก์ไม่ถูกต้อง');
        return;
      }

      try {
        // Update payment status
        const paymentRef = doc(db, 'salePagePayments', paymentId);
        const paymentDoc = await getDoc(paymentRef);

        if (!paymentDoc.exists()) {
          setStatus('error');
          setMessage('ไม่พบข้อมูลการชำระเงิน');
          return;
        }

        const paymentData = paymentDoc.data();

        // Calculate subscription end date based on billing period
        const startDate = new Date();
        const endDate = new Date();
        
        if (paymentData.billingPeriod === 'yearly') {
          endDate.setFullYear(endDate.getFullYear() + 1);
        } else {
          endDate.setMonth(endDate.getMonth() + 1);
        }

        // Update user's salepage subscription
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          'salePageSubscription.status': 'active',
          'salePageSubscription.plan': paymentData.plan || 'start',
          'salePageSubscription.startDate': startDate,
          'salePageSubscription.endDate': endDate,
          'salePageSubscription.billingPeriod': paymentData.billingPeriod,
          'salePageSubscription.verifiedAt': new Date(),
          'salePageSubscription.addons': paymentData.addons || {}
        });

        setStatus('success');
        setMessage('ยืนยันอีเมลสำเร็จ! กำลังพาคุณไปยัง Dashboard...');

        setTimeout(() => {
          navigate('/salepage/dashboard');
        }, 2000);

      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
        setMessage('เกิดข้อผิดพลาด กรุณาติดต่อทีมงาน');
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">กำลังยืนยันอีเมล</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">ยืนยันสำเร็จ!</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
            >
              กลับหน้าแรก
            </button>
          </>
        )}
      </div>
    </div>
  );
}
